import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Chip,
} from "@mui/material";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EmbededContactForm from "../EmbededContactForm";
import { getImageUrl } from "../../../utils/helpers";

const KthPatentContactSection = ({ baseConfig, sectionStyles }) => {
  const {
    priColor,
    priHoverColor,
    tertColor,
    tertHoverColor,
    secColor,
    quadColor,
    quadHoverColor,
    linkColor,
  } = baseConfig.siteBranding;

  return (
    <Box
      component="section"
      sx={{
        p: 4,
        borderRadius: 2,
        backgroundColor: "#f3f6f9",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        border: `1px solid ${tertColor}33`,
      }}
    >
      {/* Title spanning both columns */}
      <Box textAlign="center" mb={4}>
        <Typography variant="h4" fontWeight="bold" color={tertColor}>
          Secure Your Engineering & Technology Innovations
        </Typography>
        <Typography
          variant="body1"
          fontWeight="bold"
          sx={{ mt: 1, fontSize: "1.1rem" }}
        >
          For Immediate Assistance, Call Us At:{" "}
          <Chip
            label="+1-602-837-0631"
            color="primary"
            sx={{
              backgroundColor: tertColor,
              fontWeight: "bold",
              fontSize: "1rem",
              height: "32px",
              "& .MuiChip-label": {
                color: "white",
              },
            }}
          />
        </Typography>
      </Box>

      <Grid container spacing={4} alignItems="flex-start">
        {/* LEFT SIDE: Image + Overview */}
        <Grid item xs={12} md={6}>
          <Box
            display="flex"
            justifyContent="center"
            mb={3}
            sx={{
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: 2,
                boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
                pointerEvents: "none",
              },
            }}
          >
            <img
              src={getImageUrl("engr-tech-patents.png")}
              alt="Engineering & Technology Patents"
              style={{
                width: "100%",
                maxWidth: "600px",
                height: "auto",
                borderRadius: "8px",
              }}
            />
          </Box>

          <Box
            sx={{
              p: 3,
              backgroundColor: "white",
              borderRadius: 2,
              boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ color: tertColor, mb: 2 }}
            >
              Our Patent Law Services:
            </Typography>

            <List>
              {[
                "Strategic patent drafting and prosecution for maximum protection",
                "Comprehensive patent portfolio analysis and management",
                "AI-powered patent analysis with attorney oversight",
                "Patent licensing and monetization strategies",
                "Specialized expertise in wireless technology patents",
                "High-tech electrical and computer system patent protection",
                "Standard essential patent (SEP) assessment and strategy",
              ].map((text, index) => (
                <ListItem key={index} sx={{ py: 1 }}>
                  <CheckCircleOutlineIcon sx={{ color: tertColor, mr: 1.5 }} />
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight="medium">
                        {text}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>

            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                border: `1px solid ${tertColor}33`,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                color={tertColor}
              >
                Our Approach
              </Typography>
              <Typography variant="body2" paragraph sx={{ mt: 1 }}>
                We combine technical expertise with legal knowledge to develop
                strong, enforceable patents that protect your innovations and
                create value for your business. Our focus on engineering and
                technology allows us to understand your innovations deeply and
                translate them into comprehensive patent protection.
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* RIGHT SIDE: Contact Form */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={3}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
              },
            }}
          >
            <Box sx={{ p: 2, backgroundColor: tertColor, color: "white" }}>
              <Typography variant="h5" fontWeight="medium" align="center">
                Schedule Your Patent Strategy Consultation
              </Typography>
            </Box>
            <CardContent>
              <Typography variant="body2" sx={{ mb: 3, textAlign: "center" }}>
                Please complete the form below, and our team will reach out to
                schedule a consultation with a patent attorneys.
              </Typography>

              <EmbededContactForm
                dropDownOptions={[
                  "Patent Drafting & Prosecution",
                  "Patent Search & Invalidity Analysis",
                  "Patent Enforcement (C&D Letters) & Response",
                  "Patent Portfolio Analysis",
                  "Patent Licensing",
                  "Other Patent Matter",
                ]}
                submitButtonLabel="Schedule My Consultation"
                disclaimerHTML="Please note that submitting this form does not create an attorney-client relationship. Any information shared will be kept confidential."
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* What to Expect Section */}
      <Box sx={{ mt: 4 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            color: tertColor,
            borderBottom: `1px solid ${tertColor}33`,
            pb: 1,
          }}
        >
          What to Expect in Your Free Consultation
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: `1px solid ${tertColor}33`,
                borderRadius: 2,
                height: "100%",
              }}
            >
              <Box display="flex" alignItems="center" mb={1}>
                <EventAvailableIcon sx={{ color: tertColor, mr: 1.5 }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  Initial Assessment
                </Typography>
              </Box>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText primary="Review of your innovation and protection needs" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText primary="Discussion of your business goals and strategy" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText primary="Initial patentability assessment" />
                </ListItem>
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: `1px solid ${tertColor}33`,
                borderRadius: 2,
                height: "100%",
              }}
            >
              <Box display="flex" alignItems="center" mb={1}>
                <EventAvailableIcon sx={{ color: tertColor, mr: 1.5 }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  Strategic Recommendations
                </Typography>
              </Box>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText primary="Tailored patent strategy options" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText primary="International filing considerations" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText primary="Portfolio development approach" />
                </ListItem>
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: `1px solid ${tertColor}33`,
                borderRadius: 2,
                height: "100%",
              }}
            >
              <Box display="flex" alignItems="center" mb={1}>
                <EventAvailableIcon sx={{ color: tertColor, mr: 1.5 }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  Practical Next Steps
                </Typography>
              </Box>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText primary="Fee structure and timeline overview" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText primary="Information about our discounted fixed fee option" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText primary="Clear action plan for moving forward" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Special Offer Reminder */}
      <Box
        sx={{
          mt: 4,
          p: 2,
          backgroundColor: "#e8f5e9",
          borderRadius: 2,
          border: "1px solid #81c784",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" fontWeight="bold" gutterBottom color="#2e7d32">
          REMINDER: Discounted Fixed Fee Patent Services Available
        </Typography>
        <Typography variant="body1">
          Ask about our discounted fixed fee patent drafting and prosecution
          services, with additional discounts for multiple related filings.
        </Typography>
        <Button
          variant="contained"
          startIcon={<PhoneInTalkIcon />}
          sx={{
            mt: 2,
            backgroundColor: "#2e7d32",
            color: "white",
            "&:hover": {
              backgroundColor: "#1b5e20",
            },
          }}
        >
          Call Now: +1-602-837-0631
        </Button>
      </Box>
    </Box>
  );
};

export default KthPatentContactSection;
